/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import { Styles } from './styles';
import InfoDisplayGrid from './InfoDisplayGrid';
import ImageGallery from './ImageGallery';
import FeaturesList from './FeatureList';
import AgentInfo from './AgentInfo';
import DescriptionContainer from './DescriptionContainer';
import { useMediaQuery, useTheme, DialogContent } from '@mui/material';
import { Close, NavigateBefore, NavigateNext } from '@mui/icons-material';
import {
  IAuthenticatedPropertyDetails,
  IPropertyDetails
} from '@stores/Property/types';
import Loading from '@components/Loading/Loading';
import { useLocation } from 'react-router-dom';

interface PropertyDetailsCardProps {
  key: string | undefined;
  property: IPropertyDetails | IAuthenticatedPropertyDetails | null;
  showWhatsappButton?: boolean;
}

function PropertyDetailsCard({
  key,
  property,
  showWhatsappButton = false
}: PropertyDetailsCardProps) {
  const location = useLocation();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [selectedImage, setSelectedImage] = useState<string>(
    property?.foto_capa || ''
  );

  const theme = useTheme();

  const handleImageSelect = (index: number, image: string) => {
    setSelectedImageIndex(index);
    setSelectedImage(image);
  };

  const handlePrevImage = () => {
    if (!property) return;
    setSelectedImageIndex((prevIndex) => {
      const newIndex =
        prevIndex === 0 ? property.fotos_galeria.length - 1 : prevIndex - 1;
      setSelectedImage(property.fotos_galeria[newIndex].uri);
      return newIndex;
    });
  };

  const handleNextImage = () => {
    if (!property) return;
    setSelectedImageIndex((prevIndex) => {
      const newIndex =
        prevIndex === property.fotos_galeria.length - 1 ? 0 : prevIndex + 1;
      setSelectedImage(property.fotos_galeria[newIndex].uri);
      return newIndex;
    });
  };

  // Adicione este estado para controlar o zoom
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Função para abrir o zoom
  const handleOpenZoom = () => {
    setIsZoomOpen(true);
  };

  // Função para fechar o zoom
  const handleCloseZoom = () => {
    setIsZoomOpen(false);
  };

  useEffect(() => {
    if (property) {
      setSelectedImage(property.foto_capa);
      setSelectedImageIndex(0);
    }
  }, [location, property]);

  if (!property) {
    return <Loading />;
  }

  return (
    <Styles.Container>
      <Styles.FirstInfos>
        <Styles.TopImageContainer>
          <Styles.TopImage
            key={`${key}-${selectedImage}`}
            src={selectedImage}
            alt={
              property.fotos_galeria[selectedImageIndex]?.legenda ||
              `${property.titulo} - Foto ${selectedImageIndex + 1}`
            }
            onClick={handleOpenZoom}
          />
          <Styles.ButtonLeft onClick={handlePrevImage}>←</Styles.ButtonLeft>
          <Styles.ButtonRight onClick={handleNextImage}>→</Styles.ButtonRight>
        </Styles.TopImageContainer>
        <InfoDisplayGrid property={property} />
      </Styles.FirstInfos>

      <ImageGallery
        images={property.fotos_galeria}
        onImageSelect={handleImageSelect}
        selectedImageIndex={selectedImageIndex}
      />
      <DescriptionContainer
        title={property.titulo}
        description={property?.dados_descricao?.descricao || ''}
        showWhatsappButton={showWhatsappButton}
        property={property}
      />
      {property.caracteristicas.length > 0 && (
        <FeaturesList features={property.caracteristicas} />
      )}
      <Styles.FeaturesDiv>
        <AgentInfo agent={property.corretor} />
      </Styles.FeaturesDiv>
      <Styles.ZoomModal
        open={isZoomOpen}
        onClose={handleCloseZoom}
        maxWidth={false}>
        <DialogContent>
          <Styles.ZoomNavButton
            onClick={handlePrevImage}
            sx={{ left: isSmallScreen ? 8 : 16 }}>
            <NavigateBefore fontSize="large" />
          </Styles.ZoomNavButton>

          <Styles.ZoomImage
            src={selectedImage}
            alt={
              property.fotos_galeria[selectedImageIndex]?.legenda ||
              `${property.titulo} - Foto ${selectedImageIndex + 1}`
            }
          />

          <Styles.ZoomNavButton
            onClick={handleNextImage}
            sx={{ right: isSmallScreen ? 8 : 16 }}>
            <NavigateNext fontSize="large" />
          </Styles.ZoomNavButton>

          <Styles.ZoomCloseButton onClick={handleCloseZoom}>
            <Close fontSize="large" />
          </Styles.ZoomCloseButton>
        </DialogContent>
      </Styles.ZoomModal>
    </Styles.Container>
  );
}

export default PropertyDetailsCard;
